import React, { useEffect, useState } from "react";
import { AlertCircle, TrendingUp, TrendingDown, Users } from "lucide-react";

const GAUGE_MAX = 500000;

function RevenueGaugeSVG({ mrr }) {
  const clamped = Math.min(Math.max(mrr, 0), GAUGE_MAX);
  const pct = clamped / GAUGE_MAX;
  // Halvmaane: 180 graders bue. 0% = venstre (-180), 100% = hoejre (0)
  const angle = -180 + pct * 180;
  const rad = (angle * Math.PI) / 180;
  const cx = 100, cy = 90, r = 70;
  const needleX = cx + r * Math.cos(rad);
  const needleY = cy + r * Math.sin(rad);

  // Zone-farver
  const zones = [
    { start: 0, end: 0.24, color: "#ef4444" },
    { start: 0.24, end: 0.58, color: "#f59e0b" },
    { start: 0.58, end: 0.84, color: "#22c55e" },
    { start: 0.84, end: 1, color: "#3b82f6" },
  ];

  function arcPath(startPct, endPct, radius) {
    const startAngle = -180 + startPct * 180;
    const endAngle = -180 + endPct * 180;
    const s = { x: cx + radius * Math.cos((startAngle * Math.PI) / 180), y: cy + radius * Math.sin((startAngle * Math.PI) / 180) };
    const e = { x: cx + radius * Math.cos((endAngle * Math.PI) / 180), y: cy + radius * Math.sin((endAngle * Math.PI) / 180) };
    const large = endPct - startPct > 0.5 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${radius} ${radius} 0 ${large} 1 ${e.x} ${e.y}`;
  }

  return (
    <svg viewBox="0 0 200 110" width="100%" style={{ maxWidth: 300, display: "block", margin: "0 auto" }}>
      {/* Baggrundsring */}
      {zones.map((z, i) => (
        <path key={i} d={arcPath(z.start, z.end, 70)} fill="none" stroke={z.color} strokeWidth="14" opacity="0.25" />
      ))}
      {/* Aktiv arc op til nuvaerende MRR */}
      {zones.map((z, i) => {
        const s = Math.max(z.start, 0);
        const e = Math.min(z.end, pct);
        if (e <= s) return null;
        return <path key={`a${i}`} d={arcPath(s, e, 70)} fill="none" stroke={z.color} strokeWidth="14" strokeLinecap="round" />;
      })}
      {/* Naal */}
      <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx={cx} cy={cy} r="5" fill="#ffffff" />
      {/* Labels */}
      <text x="22" y="105" fill="#9ca3af" fontSize="8" textAnchor="middle">0</text>
      <text x="178" y="105" fill="#9ca3af" fontSize="8" textAnchor="middle">500k</text>
      <text x="100" y="58" fill="#ffffff" fontSize="9" textAnchor="middle">MRR</text>
    </svg>
  );
}

export default function RevenueVelocityPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch("/api/os/vaekst/revenue-velocity", { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, []);

  const card = (icon, label, value, sub) => (
    <div style={{ flex: 1, minWidth: 140, background: "rgba(255,255,255,0.05)", borderRadius: 10, padding: "12px 14px", margin: 4 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        {icon}
        <span style={{ color: "#9ca3af", fontSize: 12 }}>{label}</span>
      </div>
      <div style={{ color: "#ffffff", fontSize: 20, fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ color: "#6b7280", fontSize: 11, marginTop: 2 }}>{sub}</div>}
    </div>
  );

  if (loading) return <div style={{ color: "#9ca3af", padding: 24 }}>Henter revenue data...</div>;
  if (error) return <div style={{ color: "#ef4444", padding: 24, display: "flex", alignItems: "center", gap: 8 }}><AlertCircle size={16} /> {error}</div>;

  const { mrr_nu, growth_rate_3md, estimated_arr, klienter_til_realistisk } = data;
  const growthPos = growth_rate_3md !== null && growth_rate_3md > 0;

  return (
    <div style={{ width: "100%" }}>
      <div style={{ marginBottom: 8, color: "#9ca3af", fontSize: 13 }}>Realistisk maal: 3,5M ARR = 291.667 kr MRR</div>
      <RevenueGaugeSVG mrr={mrr_nu} />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 16 }}>
        {card(
          <TrendingUp size={14} color="#22c55e" />,
          "MRR nu",
          `${mrr_nu?.toLocaleString("da-DK")} kr`,
          `ARR estimeret: ${parseInt(estimated_arr)?.toLocaleString("da-DK")} kr`
        )}
        {card(
          growthPos ? <TrendingUp size={14} color="#22c55e" /> : <TrendingDown size={14} color="#ef4444" />,
          "3-mdr vaekst",
          growth_rate_3md !== null ? `${growth_rate_3md > 0 ? "+" : ""}${growth_rate_3md}%` : "Mangler data",
          growth_rate_3md !== null ? "vs. 3 mdr. siden" : "Ingen historisk MRR"
        )}
        {card(
          <Users size={14} color="#3b82f6" />,
          "Klienter til maal",
          `${klienter_til_realistisk} klienter`,
          "ved 1.385 kr ARPU"
        )}
      </div>
    </div>
  );
}
